import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "./api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (error) {
      // Not authenticated — expected when no valid cookie present
      if (error?.response?.status !== 401) {
        console.error("Auth check failed:", error);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.mfa_required) return data; // { mfa_required: true, challenge_id } — caller prompts for a code
    setUser(data.user);
    return data.user;
  }, []);

  const verifyMfaLogin = useCallback(async (challengeId, code) => {
    const { data } = await api.post("/auth/mfa/verify-login", { challenge_id: challengeId, code });
    setUser(data.user);
    return data.user;
  }, []);

  const signup = useCallback(async (name, email, password) => {
    const { data } = await api.post("/auth/signup", { name, email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const googleLogin = useCallback(async (credential) => {
    const { data } = await api.post("/auth/google/session", { credential });
    if (data.mfa_required) return data; // { mfa_required: true, challenge_id } — caller prompts for a code
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Logout request failed:", error);
    }
    setUser(null);
  }, []);

  // Memoise the context value so consumers don't re-render on unrelated renders.
  const value = useMemo(
    () => ({ user, setUser, loading, login, verifyMfaLogin, signup, googleLogin, logout, refresh: checkAuth }),
    [user, loading, login, verifyMfaLogin, signup, googleLogin, logout, checkAuth],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
